/**
 * TimePickerDropdown — Custom styled time picker dropdown.
 * Displays hour:minute AM/PM columns matching the app's dark/light theme.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';

interface TimePickerDropdownProps {
  /** Current value in HH:mm (24h) format, or empty string */
  value: string;
  /** Called with HH:mm (24h) format */
  onChange: (time: string) => void;
  /** Placeholder when no value */
  placeholder?: string;
  /** Additional button class overrides */
  className?: string;
}

/** Parse "HH:mm" (24h) into { hour12, minute, period } */
function parse24(time: string): { hour12: number; minute: number; period: 'AM' | 'PM' } {
  if (!time) return { hour12: 12, minute: 0, period: 'AM' };
  const [h, m] = time.split(':').map(Number);
  const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { hour12, minute: m, period };
}

/** Convert { hour12, minute, period } back to "HH:mm" (24h) */
function to24(hour12: number, minute: number, period: 'AM' | 'PM'): string {
  let h = hour12;
  if (period === 'AM' && h === 12) h = 0;
  if (period === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, ..., 55

const TimePickerDropdown: React.FC<TimePickerDropdownProps> = ({
  value,
  onChange,
  placeholder = '--:-- --',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLDivElement>(null);
  const minRef = useRef<HTMLDivElement>(null);

  const { hour12, minute, period } = parse24(value);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll selected hour/minute into view when opening
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      hourRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
      minRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
    }, 0);
  }, [open]);

  const select = useCallback((h: number, m: number, p: 'AM' | 'PM') => {
    onChange(to24(h, m, p));
  }, [onChange]);

  const displayValue = value
    ? `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`
    : placeholder;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full px-3 py-2 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer flex items-center justify-between ${className}`}
      >
        <span className={value ? '' : 'text-gray-400'}>{displayValue}</span>
        <Clock className="w-4 h-4 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-2 w-56">
          <div className="flex gap-1">
            {/* Hour column */}
            <div ref={hourRef} className="flex-1 max-h-48 overflow-y-auto scrollbar-thin">
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  data-selected={h === hour12}
                  onClick={() => select(h, minute, period)}
                  className={`w-full px-2 py-1.5 text-sm text-center rounded transition-colors ${
                    h === hour12
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                  }`}
                >
                  {String(h).padStart(2, '0')}
                </button>
              ))}
            </div>

            {/* Minute column */}
            <div ref={minRef} className="flex-1 max-h-48 overflow-y-auto scrollbar-thin">
              {MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-selected={m === minute}
                  onClick={() => select(hour12, m, period)}
                  className={`w-full px-2 py-1.5 text-sm text-center rounded transition-colors ${
                    m === minute
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                  }`}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>

            {/* AM/PM column */}
            <div className="flex flex-col gap-1">
              {(['AM', 'PM'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => select(hour12, minute, p)}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    p === period
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Clear / Done footer */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePickerDropdown;
